import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntApp } from 'antd';
import { RMk, initials } from '../components/kit';
import * as teamApi from '../api/team';

// Generation → accent colour (root = you). Mirrors the design legend.
const GEN_COLOR = ['var(--primary)', 'var(--primary)', 'var(--accent)', 'var(--navy)', 'var(--muted)'];
const genColor = (g) => GEN_COLOR[g] || 'var(--muted)';

const COL_W = 190;   // horizontal spacing per generation (left → right)
const NODE_W = 158;
const NODE_H = 60;
const ROW_GAP = 16;
const PAD = 28;

// FULL-SCREEN (landscape) org chart of the caller's downline. Escapes the 460px phone
// frame via position:fixed so it fills the viewport. Pan by scrolling; zoom with the controls.
export default function TeamHierarchyPage() {
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    teamApi.listTeamTree().then((r) => setData(r?.data ?? r)).catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build the tree (root = me; downline nodes linked by parent_id).
  const root = useMemo(() => {
    if (!data?.me) return null;
    const rootId = data.me.agent_id;
    const map = new Map();
    map.set(rootId, { ...data.me, gen: 0, self: true, children: [] });
    (data.downline || []).forEach((d) => map.set(d.agent_id, { ...d, children: [] }));
    (data.downline || []).forEach((d) => {
      const parent = map.get(d.parent_id) || map.get(rootId);
      if (parent && parent.agent_id !== d.agent_id) parent.children.push(map.get(d.agent_id));
    });
    return map.get(rootId);
  }, [data]);

  // Tidy left-to-right layout: leaves stack vertically, parents centre on their children.
  const layout = useMemo(() => {
    if (!root) return null;
    const pos = new Map();
    let leafY = PAD;
    const place = (node) => {
      const x = PAD + node.gen * COL_W;
      let y;
      if (!node.children.length) {
        y = leafY;
        leafY += NODE_H + ROW_GAP;
      } else {
        const ys = node.children.map(place);
        y = (ys[0] + ys[ys.length - 1]) / 2;
      }
      pos.set(node.agent_id, { x, y, node });
      return y;
    };
    place(root);
    const arr = [...pos.values()];
    const width = Math.max(...arr.map((p) => p.x)) + NODE_W + PAD;
    const height = Math.max(...arr.map((p) => p.y)) + NODE_H + PAD;
    const links = [];
    arr.forEach((p) => p.node.children.forEach((c) => {
      const cp = pos.get(c.agent_id);
      if (cp) links.push({ from: p, to: cp, gen: c.gen });
    }));
    return { arr, links, width, height };
  }, [root]);

  const total = (data?.downline || []).length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'var(--bg)', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar — mobile-first: back + title only; canvas chrome floats over the tree */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 15px', paddingTop: 'calc(10px + env(safe-area-inset-top))', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        <div onClick={() => nav(-1)} style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 5 8 12 15 19" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Team hierarchy</div>
        <span style={{ flex: 'none', fontSize: 11.5, fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{total} agent{total === 1 ? '' : 's'}</span>
      </div>

      {/* Canvas */}
      {!data ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
      ) : !layout || total === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '22px 20px', textAlign: 'center', maxWidth: 320 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>No downline yet</div>
            <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>Recruits you sponsor will appear here as your team grows.</div>
          </div>
        </div>
      ) : (
        <div className="scrx" style={{ flex: 1, overflow: 'auto', background: 'radial-gradient(circle at 1px 1px, var(--border) 1px, transparent 0) 0 0 / 22px 22px, var(--bg)', WebkitOverflowScrolling: 'touch' }}>
          <div style={{ width: layout.width * zoom, height: layout.height * zoom, position: 'relative' }}>
            <div style={{ width: layout.width, height: layout.height, position: 'relative', transform: `scale(${zoom})`, transformOrigin: '0 0' }}>
              {/* connectors */}
              <svg width={layout.width} height={layout.height} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                {layout.links.map((l, i) => {
                  const x1 = l.from.x + NODE_W, y1 = l.from.y + NODE_H / 2;
                  const x2 = l.to.x, y2 = l.to.y + NODE_H / 2;
                  const midX = x1 + (x2 - x1) / 2;
                  return <path key={i} d={`M${x1},${y1} H${midX} V${y2} H${x2}`} fill="none" stroke={genColor(l.gen)} strokeOpacity="0.5" strokeWidth="1.6" />;
                })}
              </svg>
              {/* nodes */}
              {layout.arr.map((p) => (
                <NodeCard key={p.node.agent_id} p={p} selected={sel?.agent_id === p.node.agent_id} onTap={setSel} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Floating canvas chrome — legend (bottom-left) + zoom (bottom-right); hidden while a node is open */}
      {data && layout && total > 0 && !sel && (
        <>
          <div style={{ position: 'absolute', left: 12, bottom: 'calc(12px + env(safe-area-inset-bottom))', zIndex: 15, display: 'flex', gap: 11, alignItems: 'center', background: 'color-mix(in srgb,var(--surface) 86%,transparent)', border: '1px solid var(--border)', borderRadius: 999, padding: '7px 13px', backdropFilter: 'blur(6px)' }}>
            {[1, 2, 3, 4].map((g) => (
              <span key={g} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, color: 'var(--muted)' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: genColor(g) }} />G{g}
              </span>
            ))}
          </div>
          <div style={{ position: 'absolute', right: 12, bottom: 'calc(12px + env(safe-area-inset-bottom))', zIndex: 15, display: 'flex', gap: 8, alignItems: 'center' }}>
            <ZoomBtn onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}>−</ZoomBtn>
            <ZoomBtn onClick={() => setZoom(1)} wide>{Math.round(zoom * 100)}%</ZoomBtn>
            <ZoomBtn onClick={() => setZoom((z) => Math.min(1.6, Math.round((z + 0.1) * 10) / 10))}>+</ZoomBtn>
          </div>
        </>
      )}

      {/* Detail popover */}
      {sel && <NodeDetail node={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

function NodeCard({ p, selected, onTap }) {
  const n = p.node;
  const self = n.self;
  return (
    <div
      onClick={() => onTap(n)}
      style={{
        position: 'absolute', left: p.x, top: p.y, width: NODE_W, height: NODE_H,
        display: 'flex', alignItems: 'center', gap: 9, padding: '0 11px', cursor: 'pointer',
        borderRadius: 13, boxSizing: 'border-box',
        background: self ? 'linear-gradient(160deg,#241854,#18345E)' : 'var(--surface)',
        color: self ? '#fff' : 'var(--text)',
        border: `1.5px solid ${selected ? 'var(--primary)' : self ? 'transparent' : 'var(--border)'}`,
        boxShadow: selected ? '0 0 0 3px color-mix(in srgb,var(--primary) 22%,transparent)' : 'none',
      }}
    >
      <div style={{ width: 30, height: 30, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, background: self ? 'rgba(255,255,255,.16)' : 'var(--chip)', color: self ? '#fff' : 'var(--primary)' }}>
        {initials(n.name)}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{self ? 'You' : n.name}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 1 }}>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: self ? 'rgba(255,255,255,.85)' : 'var(--primary)' }}>{n.tier}%</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: self ? '#9fe3c4' : 'var(--ok)', fontVariantNumeric: 'tabular-nums' }}>{RMk(n.gci)}</span>
        </div>
      </div>
      {n.children?.length > 0 && (
        <span style={{ position: 'absolute', right: -7, top: '50%', transform: 'translateY(-50%)', fontSize: 10.5, fontWeight: 700, minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid var(--surface)' }}>
          {n.children.length}
        </span>
      )}
    </div>
  );
}

function NodeDetail({ node, onClose }) {
  return (
    <div onClick={onClose} className="sheet-scrim" style={{ position: 'fixed', inset: 0, background: 'rgba(10,14,22,.35)', zIndex: 60, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 18 }}>
      <div onClick={(e) => e.stopPropagation()} className="sheet-panel" style={{ width: 'min(420px, 100%)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: '14px 16px', boxShadow: '0 12px 40px rgba(10,14,22,.28)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: '50%', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, background: 'var(--chip)', color: 'var(--primary)' }}>{initials(node.name)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{node.self ? 'You' : node.name}</div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{node.self ? 'Team root' : `Generation ${node.gen}`}</div>
          </div>
          <span onClick={onClose} style={{ cursor: 'pointer', color: 'var(--muted)', fontSize: 18, padding: '0 4px' }}>✕</span>
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
          <Metric label="GCI TIER" value={`${node.tier}%`} />
          <Metric label="LIFETIME GCI" value={RMk(node.gci)} tone="var(--ok)" />
          <Metric label="DIRECT" value={node.children?.length ?? 0} />
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value, tone }) => (
  <div style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '9px 11px' }}>
    <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '.05em', color: 'var(--muted)' }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 700, marginTop: 2, color: tone || 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
);

const ZoomBtn = ({ children, onClick, wide }) => (
  <div onClick={onClick} style={{ minWidth: wide ? 44 : 30, height: 30, padding: '0 6px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: wide ? 11 : 15, fontWeight: 600, cursor: 'pointer', flex: 'none' }}>
    {children}
  </div>
);
